import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Globe, Instagram, Mail, Music2, Sparkles, UserRound } from 'lucide-react';
import BrandGhostie from '../components/BrandGhostie';
import { ART } from '../lib/art';
import { api } from '../lib/api';
import { CONTENT_BLOG_POSTS } from '../lib/contentBlog';
import { FAKE_REVIEWS } from '../lib/reviews';

const SITE_URL = 'https://www.ghostie.it';

function PinterestIcon({ size = 17 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <path d="M12.04 2C6.62 2 3 5.71 3 10.32c0 2.14 1.2 4.81 3.12 5.66.29.13.45.07.52-.2.05-.2.31-1.25.43-1.73.04-.16.02-.3-.11-.45-.63-.76-1.14-2.15-1.14-3.45 0-3.22 2.47-6.34 6.67-6.34 3.63 0 6.17 2.48 6.17 6.02 0 4-2.02 6.78-4.64 6.78-1.45 0-2.53-1.2-2.18-2.67.42-1.75 1.23-3.64 1.23-4.9 0-1.13-.61-2.07-1.86-2.07-1.48 0-2.66 1.53-2.66 3.58 0 1.31.44 2.19.44 2.19s-1.46 6.18-1.73 7.33c-.3 1.27-.18 3.05-.05 4.23h.17c.62-.99 1.34-2.45 1.68-3.7.18-.68.93-3.53.93-3.53.49.93 1.9 1.71 3.4 1.71 4.47 0 7.61-4.11 7.61-9.22C21 5.36 17.36 2 12.04 2Z" />
    </svg>
  );
}

const socialLinks = [
  { label: 'Email', icon: Mail, href: 'mailto:hello@ghostie.it' },
  { label: 'Website', icon: Globe, href: SITE_URL },
  { label: 'Instagram', icon: Instagram, href: 'https://www.instagram.com/ghostie.it' },
  { label: 'TikTok', icon: Music2, href: 'https://www.tiktok.com/@ghostie.it' },
  { label: 'Pinterest', icon: PinterestIcon, href: 'https://www.pinterest.com/ghostietravel' },
];

function normalizePost(p) {
  return {
    ...p,
    post_id: p.post_id || p.slug,
    cover_image: p.cover_image || p.featured_image_url,
    body: p.body || p.body_markdown || '',
  };
}

function BioMascots() {
  const mascots = [
    { src: ART.wave, className: 'left-2 top-24 w-16 sm:w-20 rotate-[-12deg]' },
    { src: ART.tarot, className: 'right-2 top-40 w-16 sm:w-20 rotate-[10deg]' },
    { src: ART.coffee, className: 'left-3 bottom-40 w-16 sm:w-20 rotate-[8deg]' },
    { src: ART.sleepy, className: 'right-4 bottom-20 w-16 sm:w-20 rotate-[-8deg]' },
  ];
  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-0 overflow-visible">
      {mascots.map((item, index) => (
        <img
          key={`${item.src}-${index}`}
          src={item.src}
          alt=""
          className={`absolute opacity-75 drop-shadow-[0_18px_35px_rgba(249,115,22,0.18)] float-anim ${item.className}`}
          loading="lazy"
        />
      ))}
    </div>
  );
}

export default function Bio() {
  const [dbPosts, setDbPosts] = useState([]);

  useEffect(() => {
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    const previous = robots.getAttribute('content') || 'index,follow';
    robots.setAttribute('content', 'noindex,nofollow');
    document.title = 'Ghostie links';
    return () => robots.setAttribute('content', previous);
  }, []);

  useEffect(() => {
    api.get('/blog').then(r => setDbPosts((r.data || []).map(normalizePost))).catch(() => setDbPosts([]));
  }, []);

  const posts = useMemo(() => {
    const source = dbPosts.length ? dbPosts : CONTENT_BLOG_POSTS;
    return source.slice(0, 8);
  }, [dbPosts]);

  const reviews = FAKE_REVIEWS.slice(0, 8);

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#050505] px-4 py-8 sm:py-12">
      <BioMascots />
      <div className="absolute inset-0 z-0 bg-[radial-gradient(circle_at_top,rgba(249,115,22,0.16),transparent_32%),radial-gradient(circle_at_bottom,rgba(63,46,30,0.28),transparent_38%)]" />

      <main className="relative z-10 max-w-lg mx-auto text-center">
        <div className="mx-auto mb-5 h-32 w-32 rounded-full border border-[#3f2e1e] bg-[#111]/80 shadow-[0_20px_70px_rgba(249,115,22,0.16)] overflow-hidden">
          <img src={ART.hero} alt="Ghostie mascot" className="h-full w-full object-cover scale-125 translate-y-2 float-anim" />
        </div>

        <h1 className="font-heading text-5xl font-semibold mb-2">
          <BrandGhostie />
        </h1>
        <p className="text-[#c7c7c7] leading-relaxed mb-5">
          Meet a local. Follow the weird little door. Find the stranger side of your favourite city.
        </p>

        <div className="flex justify-center gap-3 mb-7">
          {socialLinks.map(({ label, icon: Icon, href }) => (
            <a
              key={label}
              href={href}
              target={href.startsWith('http') ? '_blank' : undefined}
              rel={href.startsWith('http') ? 'noreferrer' : undefined}
              aria-label={label}
              className="h-10 w-10 rounded-full border border-[#3f2e1e] bg-[#111]/80 flex items-center justify-center text-[#f97316] hover:bg-[#f97316] hover:text-[#0a0a0a] transition-colors"
            >
              <Icon size={17} />
            </a>
          ))}
        </div>

        <div className="space-y-3 mb-9">
          <Link to="/book" className="btn-primary w-full justify-center text-base py-4">
            <Sparkles size={18} /> Book an experience
          </Link>
          <Link to="/about" className="btn-secondary w-full justify-center text-base py-4">
            What is Ghostie?
          </Link>
          <Link to="/start" className="btn-secondary w-full justify-center text-base py-4">
            <UserRound size={18} /> Become a Ghostie
          </Link>
        </div>

        <section className="text-left">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="badge-cozy mb-2">Our blog</div>
              <h2 className="font-heading text-3xl">Dark travel tips</h2>
            </div>
            <Link to="/blog" className="text-sm text-[#f97316] hover:underline">View all</Link>
          </div>

          <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 px-4">
            {posts.map((post) => (
              <Link
                key={post.post_id}
                to={`/blog/${post.slug}`}
                className="magical-card min-w-[250px] max-w-[250px] overflow-hidden snap-start"
              >
                {post.cover_image && <img src={post.cover_image} alt={post.title} className="h-32 w-full object-cover" />}
                <div className="p-4">
                  <h3 className="font-heading text-xl leading-tight mb-2">{post.title}</h3>
                  <p className="text-xs text-[#a3a3a3] line-clamp-3">{post.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="text-left mt-8">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="badge-cozy mb-2">Reviews</div>
              <h2 className="font-heading text-3xl">Traveller whispers</h2>
            </div>
            <Link to="/reviews" className="text-sm text-[#f97316] hover:underline">View all</Link>
          </div>

          <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 px-4">
            {reviews.map((review) => (
              <Link
                key={review.review_id}
                to="/reviews"
                className="magical-card min-w-[250px] max-w-[250px] overflow-hidden snap-start"
              >
                {review.image && <img src={review.image} alt={`${review.city} traveller review`} className="h-32 w-full object-cover" />}
                <div className="p-4">
                  <div className="text-[#f97316] text-sm mb-2">{'★'.repeat(review.rating || 5)}</div>
                  <p className="text-sm text-[#c7c7c7] line-clamp-4">"{review.text}"</p>
                  <p className="mt-3 text-xs uppercase tracking-widest font-accent text-[#a3a3a3]">{review.first_name} · {review.city}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
